import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProject } from '../utils/api';

const loadCache = (id) => {
  try { return JSON.parse(sessionStorage.getItem(`project-chat-${id}`)) || null; } catch { return null; }
};

// Split message text into segments: plain text and YouTube search URL pills
const renderContent = (content) => {
  if (!content) return null;
  const re = /https:\/\/www\.youtube\.com\/results\?search_query=([^\s\n]+)/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(content)) !== null) {
    if (m.index > lastIdx) parts.push(<span key={key++}>{content.slice(lastIdx, m.index)}</span>);
    const query = decodeURIComponent(m[1].replace(/\+/g, ' '));
    parts.push(
      <a key={key++} href={m[0]} target="_blank" rel="noopener noreferrer" className="chat-yt-link">
        🎬 {query}
      </a>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < content.length) parts.push(<span key={key++}>{content.slice(lastIdx)}</span>);
  return parts.length > 0 ? parts : content;
};

const ProjectChat = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    getProject(id)
      .then(({ data }) => {
        setProject(data);
        const cached = loadCache(id);
        if (cached?.messages?.length > 0) {
          setMessages(cached.messages);
        } else {
          setMessages([{
            role: 'assistant',
            content: `I have your full "${data.title}" project loaded — materials, tools, steps, and all details. What are you working on or running into?`,
          }]);
        }
      })
      .catch(() => setLoadError('Could not load project.'));
  }, [id]);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(`project-chat-${id}`, JSON.stringify({ messages }));
    }
  }, [messages, id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  useEffect(() => {
    if (!thinking) inputRef.current?.focus();
  }, [thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setThinking(true);

    // Exclude the static welcome message from API calls
    const apiMessages = newMessages.filter((m, i) => !(i === 0 && m.role === 'assistant'));

    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const response = await fetch('/api/intake/project-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: id, messages: apiMessages }),
      });

      if (!response.ok) {
        let msg = 'Request failed';
        try { msg = (await response.json()).message || msg; } catch { /* non-JSON */ }
        throw new Error(msg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedText = '';

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(part.slice(6)); } catch { continue; }

          if (evt.type === 'text') {
            streamedText += evt.text;
            setMessages((prev) => [
              ...prev.slice(0, -1),
              { role: 'assistant', content: streamedText },
            ]);
          } else if (evt.type === 'done') {
            setThinking(false);
          } else if (evt.type === 'error') {
            throw new Error(evt.message);
          }
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: `Sorry, ${err.message || 'something went wrong'}. Please try again.` },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const clearHistory = () => {
    sessionStorage.removeItem(`project-chat-${id}`);
    setMessages([{
      role: 'assistant',
      content: `I have your full "${project.title}" project loaded — materials, tools, steps, and all details. What are you working on or running into?`,
    }]);
  };

  if (loadError) return <div className="page"><p className="empty-state">{loadError}</p></div>;
  if (!project) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Project Help</h1>
          <p className="page-subtitle">
            <Link to={`/projects/${id}`} style={{ color: 'var(--muted)', textDecoration: 'none' }}>
              ← {project.title}
            </Link>
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={clearHistory}>Clear History</button>
        </div>
      </div>

      <div className="project-chat-container">
        <div className="intake-messages project-chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`intake-msg intake-msg-${m.role}`}>
              {m.role === 'assistant' && <span className="intake-avatar">🤖</span>}
              <div className="intake-bubble">
                {m.role === 'assistant' && m.content ? renderContent(m.content) : m.content}
              </div>
            </div>
          ))}
          {thinking && messages[messages.length - 1]?.content === '' && (
            <div className="intake-msg intake-msg-assistant">
              <span className="intake-avatar">🤖</span>
              <div className="intake-bubble intake-thinking"><span /><span /><span /></div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="intake-input-row">
          <textarea
            ref={inputRef}
            className="intake-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this project… (Enter to send)"
            rows={2}
            disabled={thinking}
          />
          <button className="btn btn-primary intake-send" onClick={send} disabled={!input.trim() || thinking}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectChat;
